import axios from 'axios';
import { LoaderContext } from 'context/LoaderContextProvider';
import draftToHtml from 'draftjs-to-html';
import { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import pLimit from 'p-limit';
import { useAuth } from 'services/auth';
import { useNetwork } from 'services/network';
import {
  backHomeLink,
  JsonApiEditorlocalClientId,
  JsonApiEditorlocalClientSecret,
  JsonApiEditorlocalPassword,
  JsonApiEditorlocalUsername,
} from 'utils/constants';
import translations from 'utils/translations';
import {
  accordionChildren,
  AutocompleteEnum,
  BundleJsonApiStates,
  BundlesEnum,
  BundleStates,
  FieldTypeEnum,
  FormSubmitEnum,
  ParagraphEnum,
} from '../constants';
import {
  parseDataToPreview,
  parseEntityWithReactFC,
  parseHtmlToDraw,
  parseFetchedEntity,
} from '../utils';
import {
  createDesigner,
  createFreeImage,
  createFullScreenFreeImage,
  createQuotedText,
  createTitleSection,
  createSdBrandMultitext,
  createSlider,
  createTechnicalSheet,
  createVimeoVideo,
  createVirtualShowroom,
  createYoutubeVideo,
  editDesigner,
  editFreeImage,
  editFullScreenFreeImage,
  editQuotedText,
  editTitleSection,
  editSdBrandMultitext,
  editSlider,
  editTechnicalSheet,
  editVimeoVideo,
  editVirtualShowroom,
  editYoutubeVideo,
} from './additionalContentsUtils';
import { isAfter } from 'date-fns';

const limit = pLimit(1);

const logApiError = (err, request) => {
  let errorMessage = `🔴 API ERROR`;

  if (err.statusCode) errorMessage += ` ${err.statusCode}`;
  if (request) errorMessage += ` | ${request}`;
  if (err.message) errorMessage += `: ${err.message}`;

  console.error(errorMessage);
};

const isLocal = process.env.NODE_ENV === 'development';

const useEntities = () => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const { setLoaderActive } = useContext(LoaderContext);
  const networkHelper = useNetwork();
  const { getCurrentLoggedUserParams } = useAuth();
  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  // Constants
  const baseUrl = backHomeLink;
  // States
  const [brandName, setBrandName] = useState();
  const [generalAccordionComponents, setGeneralAccordionComponents] =
    useState();
  const [basicAccordionComponents, setBasicAccordionComponents] = useState();
  const [additionalContents, setAdditionalContents] = useState();
  const [additionalComponents, setAdditionalComponents] = useState();
  const [requiredFields, setRequiredFields] = useState();
  const [previewData, setPreviewData] = useState({});
  const [customErrors, setCustomErrors] = useState();
  const [open, setOpen] = useState(false);
  const [btnCliked, setBtnClicked] = useState();
  const [submittedData, setSubmittedData] = useState();
  const [csrfToken, setCsrfToken] = useState();

  const getCsrfToken = async ({ useLoader = false, langCode }) => {
    try {
      if (csrfToken) return csrfToken;
      useLoader && showLoader();
      const res = await networkHelper.get(
        `${baseUrl}/${langCode}/session/token`
      );
      setCsrfToken(res?.data);
      return res?.data;
    } catch (err) {
      logApiError(err, 'get-csrf-token');
    } finally {
      useLoader && hideLoader();
    }
  };

  const editEntity = async ({
    useLoader = false,
    langCode,
    body = {},
    entity = 'node',
    bundle = 'prodotto',
    entityId = '',
  }) => {
    try {
      useLoader && showLoader();
      const token = await getCsrfToken({ useLoader: false, langCode });
      const res = await networkHelper.patch(
        `${baseUrl}/${langCode}/jsonapi/${entity}/${bundle}/${entityId}`,
        body,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            'X-CSRF-Token': token,
          },
          withCredentials: true,
        }
      );
      return res?.data;
    } catch (err) {
      logApiError(err, `edit-${entity}-${bundle}-${entityId}`);
    } finally {
      useLoader && hideLoader();
    }
  };

  const createEntity = async ({
    useLoader = false,
    langCode,
    body = {},
    entity = 'node',
    bundle = 'prodotto',
    fieldName = '',
    customHeaders = {},
    useAxios = true,
    uuid = '',
  }) => {
    try {
      useLoader && showLoader();
      const token = await getCsrfToken({ useLoader: false, langCode });
      const res = useAxios
        ? await networkHelper.post(
            `${baseUrl}/${langCode}/jsonapi/${entity}/${bundle}${
              uuid ? `/${uuid}` : ''
            }${fieldName ? `/${fieldName}` : ''}`,
            body,
            {
              headers: {
                Accept: 'application/vnd.api+json',
                'Content-Type': 'application/vnd.api+json',
                'X-CSRF-Token': token,
                ...customHeaders,
              },
              withCredentials: true,
            }
          )
        : await fetch(
            `${baseUrl}/${langCode}/jsonapi/${entity}/${bundle}${
              uuid ? `/${uuid}` : ''
            }${fieldName ? `/${fieldName}` : ''}`,
            {
              method: 'POST',
              headers: {
                Accept: 'application/vnd.api+json',
                'Content-Type': 'application/vnd.api+json',
                'X-CSRF-Token': token,
                ...customHeaders,
              },
              body,
            }
          ).then((response) => response.json());

      return res?.data;
    } catch (err) {
      logApiError(err, `create-${entity}-${bundle}`);
    } finally {
      useLoader && hideLoader();
    }
  };

  const createSubReq = async ({ useLoader = false, bluePrints, langCode }) => {
    try {
      useLoader && showLoader();
      const token = await getCsrfToken({ useLoader: false, langCode });
      const targetsConfig = {
        method: 'post',
        url: `${baseUrl}/subrequests?_format=json`,
        headers: {
          'X-CSRF-Token': token,
          withCredentials: true,
        },
        data: JSON.stringify(bluePrints),
      };
      const res = await axios(targetsConfig);
      return {
        responses: Object.values(res?.data)
          .map((req) => (req.body ? JSON.parse(req.body) : undefined))
          .filter((r) => !!r),
        positions: Object.entries(res?.data)
          .map(([k, v]) => ({
            position: +k.split('-').pop(),
            id: v.body ? JSON.parse(v.body)?.data?.id : undefined,
          }))
          .filter((r) => !!r.id),
      };
    } catch (err) {
      logApiError(err, `create-sub-req`);
    } finally {
      useLoader && hideLoader();
    }
  };

  const getEntity = async ({
    useLoader = false,
    langCode,
    entity = 'node',
    bundle = 'prodotto',
    bundleId = '',
    resourceVersion = 'rel:working-copy',
    include = 'field_content.field_slider_detail',
  }) => {
    try {
      useLoader && showLoader();
      const workingCopy = await networkHelper.get(
        `${baseUrl}/${langCode}/jsonapi/${entity}/${bundle}/${bundleId}?resourceVersion=${resourceVersion}${
          include && `&include=${include}`
        }`
      );
      const latestVersion = await networkHelper.get(
        `${baseUrl}/${langCode}/jsonapi/${entity}/${bundle}/${bundleId}?resourceVersion=rel:latest-version${
          include && `&include=${include}`
        }`
      );
      // Logica per cercare sempre di ritornare la versione più nuova
      const lVDate = latestVersion?.data?.data?.attributes?.revision_timestamp;
      const wCDate = workingCopy?.data?.data?.attributes?.revision_timestamp;
      const jsDateLvDate = new Date(lVDate);
      const jsDateWcDate = new Date(wCDate);
      if (jsDateLvDate && jsDateWcDate) {
        const isLatestVersionAfter = isAfter(
          new Date(jsDateLvDate),
          new Date(jsDateWcDate)
        );
        // Se è più nuova la Latest, torno quella
        if (isLatestVersionAfter) return latestVersion?.data;
        // Altrimenti torno la working
        else return workingCopy?.data;
      } else {
        // Se non esiste almeno una delle due Date, ritorno la Working Copy
        return workingCopy?.data;
      }
    } catch (err) {
      if (resourceVersion === 'rel:latest-version') {
        return await getEntity({
          useLoader,
          bundleId,
          bundle,
          include,
          langCode,
          resourceVersion: 'rel:working-copy',
        });
      }
      logApiError(err, `get-${entity}-${bundle}-${bundleId}`);
    } finally {
      useLoader && hideLoader();
    }
  };

  const getViews = async ({
    useLoader = false,
    langCode,
    entity = 'espositori_stanza',
    bundle = 'field_s_classe_merceologica_target_id',
    bundleId = 31,
  }) => {
    try {
      useLoader && showLoader();
      const res = await networkHelper.get(
        `${baseUrl}/${langCode}/jsonapi/views/taxonomy_terms/block?views-filter[vid]=${entity}&views-filter[${bundle}]=${bundleId}`
      );
      return res?.data;
    } catch (err) {
      logApiError(err, `get-${entity}-${bundle}-${bundleId}`);
    } finally {
      useLoader && hideLoader();
    }
  };

  const getExhibitorBrands = async ({
    useLoader = false,
    langCode,
    viewId = 'brand',
    viewDisplay = 'entity_reference_1',
  }) => {
    try {
      useLoader && showLoader();
      const res = await networkHelper.get(
        `${baseUrl}/${langCode}/jsonapi/views/${viewId}/${viewDisplay}`
      );
      return res?.data;
    } catch (err) {
      logApiError(err, `get-${viewId}-${viewDisplay}`);
    } finally {
      useLoader && hideLoader();
    }
  };

  const listEntityFieldsForm = async ({
    locale,
    bundle,
    productLangCode = 'it',
  }) => {
    showLoader();
    await getCsrfToken({ useLoader: false, langCode: locale });
    const params = {
      entityTpe: 'node',
      bundle,
      langcode: locale,
      fetchParagraphs: true,
      ...(isLocal && {
        baseUrl: baseUrl,
        localClientId: JsonApiEditorlocalClientId,
        localClientSecret: JsonApiEditorlocalClientSecret,
        localUsername: JsonApiEditorlocalUsername,
        localPassword: JsonApiEditorlocalPassword,
      }),
    };
    const brand = await getCurrentLoggedUserParams();
    setBrandName({ brandName: brand?.field_brand_name || 'brand' });

    try {
      const res = await networkHelper.fetchEntityFieldsForm(params);
      const _field = res.fieldConfig.data;
      const _form = res.formConfig.data[0];
      const _additionalContent = res.additionalContent;
      let _additionalContentComponents = [];
      if (_additionalContent) {
        const contents = Object.values(_additionalContent)
          .map((content) => {
            const parsedContent = JSON.parse(content.body);
            return parsedContent.data;
          })
          .flat()
          .reduce((a, b) => {
            const name = b.attributes?.bundle;
            const isForm = !!b.attributes?.content;
            if (!a[name]) a[name] = { fieldConfig: [], formDisplay: {} };
            isForm ? (a[name].formDisplay = b) : a[name].fieldConfig.push(b);
            return a;
          }, {});
        const _additionalComponents = {};
        Object.entries(contents)
          .map(([k, v]) => {
            const components = parseEntityWithReactFC({
              entities: v.fieldConfig,
              forms: v.formDisplay,
            });
            _additionalComponents[k] = components;
          })
          .flat();

        setAdditionalContents(_additionalComponents);
        _additionalContentComponents = _additionalComponents
          ? Object.values(_additionalComponents).flat()
          : [];
        setAdditionalComponents(_additionalContentComponents);
      }
      if (_field?.length && _form) {
        const parsedFieldConfig = parseEntityWithReactFC({
          entities: _field,
          forms: _form,
        });
        const _requiredFields = [
          ...parsedFieldConfig,
          ..._additionalContentComponents,
        ]
          .filter((component) => component.isRequired)
          .map((c) => {
            return c?.bundle === 'youtube_video' ? `${c.name}_youtube` : c.name;
          });
        const _generalComponents = parsedFieldConfig.filter(
          (component) =>
            !!accordionChildren[bundle].general.find(
              (field) => field === component?.name
            )
        );

        const _basicComponents = parsedFieldConfig.filter(
          (component) =>
            !!accordionChildren[bundle].basic.find(
              (field) => field === component?.name
            )
        );

        // Gestiamo il flusso dele categorie
        const foundProductClasses = _generalComponents.find(
          (comp) => comp.formEntityBundle === AutocompleteEnum.productClasses
        );
        if (foundProductClasses) {
          const productClassesParams = {
            entityTpe: foundProductClasses.formEntityType,
            bundle: foundProductClasses.formEntityBundle,
            langcode: locale,
            isTaxonomy: true,
            ...(isLocal && {
              baseUrl: baseUrl,
              localClientId: JsonApiEditorlocalClientId,
              localClientSecret: JsonApiEditorlocalClientSecret,
              localUsername: JsonApiEditorlocalUsername,
              localPassword: JsonApiEditorlocalPassword,
            }),
          };
          const productClassesRes = await networkHelper.fetchEntityFieldsForm(
            productClassesParams
          );
          const _productClassesRes = productClassesRes?.fieldConfig?.data || [];
          const _productClassesOptions = _productClassesRes.map((p) => ({
            value: p?.attributes?.drupal_internal__tid || '',
            label: p?.attributes?.name || '',
            entityId: p?.id,
          }));
          const prodClassIdx = [..._generalComponents].findIndex(
            (el) => el.formEntityBundle === AutocompleteEnum.productClasses
          );
          if (prodClassIdx >= 0)
            _generalComponents[prodClassIdx] = {
              ...foundProductClasses,
              options:
                _productClassesOptions.sort((a, b) =>
                  a.label.localeCompare(b.label)
                ) || [],
            };
        }

        // Gestiamo il flusso del Brand
        const foundBrand = _basicComponents.find(
          (comp) => comp.name === 'field_brand'
        );
        if (foundBrand) {
          const brandRes = await getExhibitorBrands({
            useLoader: false,
            langCode: productLangCode,
            viewId: foundBrand.formBundle.view.view_name,
            viewDisplay: foundBrand.formBundle.view.display_name,
          });
          const _brandRes = brandRes?.data || [];
          const _brandOptions = _brandRes.map((p) => ({
            label:
              `${p?.attributes?.title} (${p?.attributes?.path?.alias || ''})` ||
              '',
            value: p?.id,
          }));
          const brandIdx = [..._basicComponents].findIndex(
            (el) => el.name === 'field_brand'
          );
          if (brandIdx >= 0)
            _basicComponents[brandIdx] = {
              ...foundBrand,
              options:
                _brandOptions.sort((a, b) => a.label.localeCompare(b.label)) ||
                [],
            };
        }

        setRequiredFields([..._requiredFields, 'title']);
        setGeneralAccordionComponents(_generalComponents);
        setBasicAccordionComponents(_basicComponents);
      }
    } catch (error) {
      logApiError(error, 'list-entity-fields-form');
    } finally {
      hideLoader();
    }
  };

  const listEntityReferenceRevisionsFieldsForm = async ({
    formBundle,
    formEntity = 'paragraph',
    locale,
  }) => {
    const params = {
      formBundle,
      formEntity,
      langcode: locale,
      ...(isLocal && {
        baseUrl: baseUrl,
        localClientId: JsonApiEditorlocalClientId,
        localClientSecret: JsonApiEditorlocalClientSecret,
        localUsername: JsonApiEditorlocalUsername,
        localPassword: JsonApiEditorlocalPassword,
      }),
    };
    try {
      const res = await networkHelper.fetchEntityReferenceRevisionsFieldsForm(
        params
      );
      const contents = Object.values(res?.data)
        .map((content) => {
          const parsedContent = JSON.parse(content.body);
          return parsedContent.data;
        })
        .flat()
        .reduce((a, b) => {
          const name = b.attributes?.bundle;
          const isForm = !!b.attributes?.content;
          if (!a[name]) a[name] = { fieldConfig: [], formDisplay: {} };
          isForm ? (a[name].formDisplay = b) : a[name].fieldConfig.push(b);
          return a;
        }, {});
      const _additionalComponents = {};
      Object.entries(contents)
        .map(([k, v]) => {
          const components = parseEntityWithReactFC({
            entities: v.fieldConfig,
            forms: v.formDisplay,
          });
          _additionalComponents[k] = components;
        })
        .flat();
      return _additionalComponents;
    } catch (error) {
      logApiError(error, 'list-entity-reference-revisions-fields-form');
    } finally {
      hideLoader();
    }
  };

  const handleRequiredData = ({ allComponents, additionalComponents }) => {
    const drawerComponents = [...allComponents, ...additionalComponents];
    const purgedData = {};
    const _copycat = { ...submittedData };
    let requiredPurged = [...requiredFields];
    additionalComponents.map((additionalComp) => {
      const bundle = additionalComp.bundle;
      if (
        !submittedData.additionalContents?.find((ac) => ac?.name === bundle)
      ) {
        // Se non è stato submittato alcun techicalSheet ad esempio, va tolto dalle logiche di required
        requiredPurged = requiredPurged.filter((el) =>
          bundle === 'youtube_video'
            ? el !== `${additionalComp.name}_youtube`
            : el !== additionalComp.name
        );
      }
    });
    submittedData?.additionalContents?.map((val) => {
      const key = Object.keys(val).filter(
        (k) =>
          k !== 'id' && k !== 'name' && k !== 'position' && k !== 'revisionId'
      );
      key.length
        ? key.map(
            (k) =>
              (_copycat[val.name === 'youtube_video' ? `${k}_youtube` : k] =
                val[k])
          )
        : (_copycat[key] = val[key]);
    });
    if (_copycat.additionalContents)
      purgedData['additionalContents'] = _copycat.additionalContents;
    Object.entries(_copycat).map(([k, v]) => {
      const found = drawerComponents.find(
        (c) => c.name === k || `${c.name}_youtube` === k
      );
      if (found) {
        if (
          found.field_type === FieldTypeEnum.IMAGE ||
          (found.field_type === FieldTypeEnum.ENTITY_REFERENCE &&
            found.formEntityBundle === AutocompleteEnum.productCategories) ||
          // TODO: understnad if only this check is suff
          (v && Array.isArray(v))
        ) {
          v?.length ? (purgedData[k] = v) : null;
        } else {
          v ? (purgedData[k] = v) : null;
        }
      }
    });

    // 2 - Fare un check con i campi richiesti
    const missingReqElements = requiredPurged
      .filter((el) => !Object.keys(purgedData).find((k) => k === el))
      .map(
        (el) =>
          drawerComponents.find(
            (comp) => el === comp.name || el === `${comp.name}_youtube`
          )?.label
      );

    return { purgedData, missingReqElements };
  };

  const submitTextOrRichText = (comp, value) =>
    comp.format
      ? {
          value:
            typeof value === 'string'
              ? draftToHtml(JSON.parse(value))
              : draftToHtml(value),
          format: comp.format,
        }
      : value;

  const onSubmitData = async (btn, locale) => {
    try {
      showLoader();
      // 1 - Se il Bottone è preview, non va iniziata tutta la logica di creazione
      // va chiuso il drawer e mostrati i dati in live action
      if (btn === FormSubmitEnum.PREVIEW || !btn) {
        const parsedPreviewData = parseDataToPreview(submittedData);
        return setPreviewData(parsedPreviewData);
      }

      const allComponents = [
        ...generalAccordionComponents,
        ...basicAccordionComponents,
        {
          name: 'title',
          field_type: FieldTypeEnum.STRING,
          isRequired: true,
          label: i18n.errors.requiredElements.productName,
        },
      ];

      // 2 - Pulire l'input da valori vuoti, array vuoti e vari
      // Fare un check con i campi richiesti
      // Se qualche campo required non è compilato, torno la modale di errore
      const { purgedData, missingReqElements } = handleRequiredData({
        allComponents,
        additionalComponents,
      });

      if (missingReqElements?.length) {
        hideLoader();
        return setCustomErrors({
          i18n: {
            title: i18n.errors.required.title,
            body: i18n.errors.required.body,
          },
          requiredMembers: missingReqElements,
        });
      }

      const dataSheets = purgedData?.additionalContents?.filter(
        (c) => c.name === ParagraphEnum.TECHNICAL_SHEET
      );

      if (dataSheets?.length) {
        const foundWithError = dataSheets.find(
          (ds) =>
            (!!ds.field_modello_3d && !ds.field_modello_3d.includes('http')) ||
            (!!ds.field_website && !ds.field_website.includes('http')) ||
            (!!ds.field_collezione && !ds.field_collezione.includes('http'))
        );
        if (foundWithError) {
          hideLoader();
          return setCustomErrors({
            i18n: {
              title: i18n.errors.urlFormat.title,
              body: i18n.errors.urlFormat.body,
            },
            requiredMembers: [],
          });
        }
      }

      const attributes = [];
      const relationships = [];
      const imagesPromises = [];
      const imagesRealtionshipsBodies = [];
      const taxonomyPromises = [];
      const taxonomyKeys = [];
      const basicInput = {
        data: {
          type: 'node--prodotto',
          attributes: {
            field_save_count: '0',
            moderation_state:
              BundleJsonApiStates[BundlesEnum.PRODUCT][BundleStates.DRAFT],
            default_langcode: true,
          },
          relationships: {
            field_content: {},
          },
        },
      };

      allComponents.map((f) => {
        const type = f.field_type;
        // Se il tipo è diverso da uno di questi,possiamo aggiungere direttamente il valore agli attributes del node--product
        // altrimenti dobbiamo prima creare il paragrafo figlio e infine il prodotto padre
        !Object.values(ParagraphEnum).includes(f?.bundle) &&
        type !== FieldTypeEnum.ENTITY_REFERENCE &&
        type !== FieldTypeEnum.ENTITY_REFERENCE_REVISIONS &&
        type !== FieldTypeEnum.FILE &&
        type !== FieldTypeEnum.IMAGE
          ? attributes.push(f)
          : relationships.push(f);
      });

      const subReqBluePrint = [];

      Object.entries(purgedData).map(([k, v]) => {
        const foundInAttributes = attributes.find((a) => {
          return a.name === k;
        });

        const foundInRelationships = relationships.find((a) => {
          return a.name === k;
        });
        const value = v === '0' ? false : v === '1' ? true : v;

        if (foundInAttributes) {
          // Il valore è un attributes, quindi dobbiamo solo controllare se ha un format da dichiarare
          basicInput.data.attributes[k] = submitTextOrRichText(
            foundInAttributes,
            value
          );
        } else if (foundInRelationships?.field_type === FieldTypeEnum.IMAGE) {
          // Se è un'immagine, va prima caricata su Drupal
          if (value.length) {
            value.map((v, idx) => {
              const _copy = new File([v], v.name);
              if (_copy.preview) delete _copy.preview;
              imagesPromises.push(
                limit(() =>
                  createEntity({
                    langCode: 'it',
                    customHeaders: {
                      'Content-Type': 'application/octet-stream',
                      Accept: 'application/vnd.api+json',
                      'Content-Disposition': `file; filename="${_copy.name}"`,
                    },
                    body: _copy,
                    fieldName: k,
                    useAxios: false,
                  })
                )
              );
              // Poi va aggiunta alle relationships
              imagesRealtionshipsBodies.push({
                key: k,
                alt: submittedData?.[`alt_${k}_${idx}`] || _copy.name,
              });
            });
          }
        } else if (
          foundInRelationships?.field_type === FieldTypeEnum.ENTITY_REFERENCE
        ) {
          const getObject = (val, ent) => ({
            type: `${ent.formEntityType}--${ent.formEntityBundle}`,
            id: val.entityId,
          });
          if (k === 'field_private_collezione') {
            // È una collezione (taxonomy_term),
            // Se essite va su
            if (value.value) {
              const parsedValue = {
                ...value,
                entityId: value.entityId || value.value,
              };
              basicInput.data.relationships[k] = {
                data: getObject(parsedValue, foundInRelationships),
              };
            } else {
              // Altrimenti prima la creo
              if (value) {
                const input = {
                  data: {
                    type: `${foundInRelationships.formEntityType}--${foundInRelationships.formEntityBundle}`,
                    attributes: {
                      name: value,
                    },
                  },
                };

                taxonomyPromises.push(
                  limit(() =>
                    createEntity({
                      useLoader: false,
                      langCode: 'it',
                      body: input,
                      entity: foundInRelationships.formEntityType,
                      bundle: foundInRelationships.formEntityBundle,
                    })
                  )
                );
                taxonomyKeys.push(k);
              }
            }
          } else {
            basicInput.data.relationships[k] = {
              data:
                // Il Brand è un caso troppo particolare, va gestito separatamente
                typeof value === 'string' && k === 'field_brand'
                  ? {
                      type: 'node--scheda_espositore',
                      id: value,
                    }
                  : value && Array.isArray(value) && value.length
                  ? value.map((v) => getObject(v, foundInRelationships))
                  : getObject(value, foundInRelationships),
            };
          }
        }
      });
      if (taxonomyPromises.length) {
        const taxonomyRes = await Promise.all(taxonomyPromises);
        taxonomyRes.map((r, idx) => {
          const taxId = r.data.id;
          const taxType = r.data.type;
          basicInput.data.relationships[taxonomyKeys[idx]] = {
            data: {
              type: taxType,
              id: taxId,
            },
          };
        });
      }
      const hasAdditionalContents = purgedData.additionalContents?.length;

      const additionalContentsPromises = [];
      if (hasAdditionalContents) {
        purgedData.additionalContents.map((c) => {
          // È un paragraph 😱
          // TODO: È un paragraph: va prima creato il paragrafo con Subrequests e, se siamo all'ultimo step, in conclusione va messa in coda la  Creazione del prodotto - Prima gestiamo solo i testi :: non posso gestire tutte le immagini dentro questo mapping
          if (c.name === ParagraphEnum.SD_BRAND_MULTITEXT) {
            createSdBrandMultitext({
              submittedData: c,
              additionalComponents,
              bluePrintArray: subReqBluePrint,
              locale: 'it',
              position: c.position,
            });
          } else if (c.name === ParagraphEnum.VIMEO_VIDEO) {
            createVimeoVideo({
              submittedData: c,
              bluePrintArray: subReqBluePrint,
              locale: 'it',
              position: c.position,
            });
          } else if (c.name === ParagraphEnum.YOUTUBE_VIDEO) {
            createYoutubeVideo({
              submittedData: c,
              additionalComponents,
              bluePrintArray: subReqBluePrint,
              locale: 'it',
              position: c.position,
            });
          } else if (c.name === ParagraphEnum.SDM_QUOTED_TEXT) {
            createQuotedText({
              submittedData: c,
              bluePrintArray: subReqBluePrint,
              locale: 'it',
              position: c.position,
            });
          } else if (c.name === ParagraphEnum.TITLE_SECTION) {
            createTitleSection({
              submittedData: c,
              bluePrintArray: subReqBluePrint,
              locale: 'it',
              position: c.position,
            });
          } else if (c.name === ParagraphEnum.VIRTUAL_SHOWROOM) {
            createVirtualShowroom({
              submittedData: c,
              bluePrintArray: subReqBluePrint,
              locale: 'it',
              position: c.position,
            });
          }
        });
        //      Gestione Contenuti con Immagine

        /**
         * Designer
         */
        const designerFields = purgedData.additionalContents.filter(
          (c) => c.name === ParagraphEnum.DESIGNER
        );
        if (designerFields?.length) {
          designerFields.map((designerField) =>
            additionalContentsPromises.push(
              limit(() =>
                createDesigner({
                  submittedData: designerField,
                  createEntityFn: createEntity,
                  bluePrintArray: subReqBluePrint,
                  locale: 'it',
                  position: designerField.position,
                })
              )
            )
          );
        }

        /**
         * Free Image FullScreen
         */
        const freeImgFullScreenFields = purgedData.additionalContents.filter(
          (c) => c.name === ParagraphEnum.FREE_IMAGE_FULL_SCREEN
        );
        if (freeImgFullScreenFields?.length) {
          freeImgFullScreenFields.map((freeImgFullScreenField) =>
            additionalContentsPromises.push(
              limit(() =>
                createFullScreenFreeImage({
                  submittedData: freeImgFullScreenField,
                  createEntityFn: createEntity,
                  bluePrintArray: subReqBluePrint,
                  locale: 'it',
                  position: freeImgFullScreenField.position,
                })
              )
            )
          );
        }

        /**
         * Free Image
         */
        const freeImgFields = purgedData.additionalContents.filter(
          (c) => c.name === ParagraphEnum.FREE_IMAGE
        );
        if (freeImgFields?.length) {
          freeImgFields.map((freeImgField) =>
            additionalContentsPromises.push(
              limit(() =>
                createFreeImage({
                  submittedData: freeImgField,
                  additionalComponents,
                  createEntityFn: createEntity,
                  bluePrintArray: subReqBluePrint,
                  locale: 'it',
                  position: freeImgField.position,
                })
              )
            )
          );
        }

        /**
         * Slider
         */
        const sliderFields = purgedData.additionalContents.filter(
          (c) => c.name === ParagraphEnum.SLIDER_DETAIL
        );
        if (sliderFields?.length) {
          sliderFields.map((sliderField) =>
            additionalContentsPromises.push(
              limit(() =>
                createSlider({
                  submittedData: sliderField,
                  additionalComponents,
                  createEntityFn: createEntity,
                  bluePrintArray: subReqBluePrint,
                  locale: 'it',
                  position: sliderField.position,
                })
              )
            )
          );
        }

        /**
         * Technical Sheet
         */
        const technicalSheetFields = purgedData.additionalContents.filter(
          (c) => c.name === ParagraphEnum.TECHNICAL_SHEET
        );
        if (technicalSheetFields?.length) {
          technicalSheetFields.map((technicalSheetField) =>
            additionalContentsPromises.push(
              limit(() =>
                createTechnicalSheet({
                  submittedData: technicalSheetField,
                  createEntityFn: createEntity,
                  bluePrintArray: subReqBluePrint,
                  locale: 'it',
                  position: technicalSheetField.position,
                })
              )
            )
          );
        }
      }

      if (imagesPromises.length) {
        // 1 Crea tutte le immagini
        const res = await Promise.all(imagesPromises);
        // 2 Recupera tutti gli id dalle immagini per crearne la corrispondente relationships
        res.map((r, idx) => {
          const imageId = r.id;
          const field_key = imagesRealtionshipsBodies[idx].key;
          const field_alt = imagesRealtionshipsBodies[idx].alt;
          basicInput.data.relationships[field_key] = {
            data: {
              type: 'file--file',
              id: imageId,
              meta: {
                alt: field_alt || field_key,
              },
            },
          };
        });
      }

      await Promise.all(additionalContentsPromises);

      if (subReqBluePrint.length) {
        const res = await createSubReq({
          bluePrints: subReqBluePrint,
          langCode: 'it',
        });
        if (res && res?.responses?.length) {
          const data = res.responses
            .map(
              (r) =>
                r.data?.id && {
                  type: r.data.type,
                  id: r.data.id,
                  meta: {
                    target_revision_id:
                      r.data.attributes.drupal_internal__revision_id,
                  },
                }
            )
            .filter((r) => !!r);
          const sortedData = [];
          data.map((d) => {
            const foundPosition = res?.positions?.find(
              (p) => p.id === d.id
            )?.position;
            sortedData.splice(foundPosition, 0, d);
          });
          basicInput.data.relationships.field_content = { data: sortedData };
        }
      }

      // console.log('LOG::   basicInput', basicInput);

      if (
        !!Object.keys(basicInput.data.relationships).length ||
        !!Object.keys(basicInput.data.attributes).length
      ) {
        const res = await createEntity({
          useLoader: false,
          langCode: 'it',
          body: basicInput,
          entity: 'node',
          bundle: 'prodotto',
        });
        // return hideLoader();
        if (btn === FormSubmitEnum.BACK) {
          window.location.href = `${baseUrl}/${locale}/admin/sdm/content`;
        } else {
          const id = res.data.id;
          // TODO: handle real node
          window.location.href = `${baseUrl}/${locale}/sdm-editor/node/prodotto/${id}?langcode=IT`;
        }
        // const parsedPreviewData = parseDataToPreview(submittedData);
        // const _wId = { ...parsedPreviewData };
        // resetFn(submittedData);
        // setPreviewData(_wId);
      }
    } catch (error) {
      logApiError(error, 'submit-data');
      setCustomErrors({
        i18n: {
          title: i18n.errors.generalError.title,
          body: i18n.errors.generalError.body,
        },
        requiredMembers: [],
      });
    } finally {
      hideLoader();
      setBtnClicked('');
    }
  };

  const onEditData = async (
    btn,
    fetchedNode,
    resetFn,
    locale,
    sistemLang,
    bundle,
    redirectLink = ''
  ) => {
    try {
      showLoader();
      // 1 - Se il Bottone è preview, non va iniziata tutta la logica di creazione
      // va chiuso il drawer e mostrati i dati in live action
      if (btn === FormSubmitEnum.PREVIEW || !btn) {
        const parsedPreviewData = parseDataToPreview(submittedData);
        const _wId = { ...parsedPreviewData, id: fetchedNode?.id };
        hideLoader();
        return setPreviewData(_wId);
      }
      const isProduct = bundle === BundlesEnum.PRODUCT;
      // La logica di partenza è la stessa della create:: tolgo i campi non compilati
      // e checko che tutti i required siano presenti
      const allComponents = [
        ...generalAccordionComponents,
        ...basicAccordionComponents,
        {
          name: 'title',
          field_type: FieldTypeEnum.STRING,
          isRequired: true,
          label: isProduct
            ? i18n.errors.requiredElements.productName
            : i18n.errors.requiredElements.brandName,
        },
      ];

      const otherLangNode = await getEntity({
        useLoader: false,
        langCode: locale === 'it' ? 'en' : 'it',
        bundleId: fetchedNode?.id,
        bundle: bundle,
        // Handle Dynamic
        include: isProduct
          ? 'field_cover, field_immagine_anteprima, field_classe_merceologica, field_sotto_categoria, field_categoria, field_private_collezione, field_content, field_content.field_image, field_content.field_immagine, field_content.field_slider_detail.bp_image_field, field_content.field_sheet_image, field_content.field_designer, field_content.field_materiali, field_content.field_caratteristiche, field_content.field_immagine_libera_full'
          : 'menu_link, field_contents, field_contents.field_slider_detail.bp_image_field, field_cover, field_logo, field_stanze',
      });
      const { additionalEntity: otherLangAdditionalEntity } =
        await parseFetchedEntity({
          entityResponse: otherLangNode,
          components: allComponents,
        });

      // 2 - Pulire l'input da valori vuoti, array vuoti e vari
      // Fare un check con i campi richiesti
      // Se qualche campo required non è compilato, torno la modale di errore
      const { missingReqElements } = handleRequiredData({
        allComponents,
        additionalComponents,
      });

      if (missingReqElements?.length) {
        hideLoader();
        return setCustomErrors({
          i18n: {
            title: i18n.errors.required.title,
            body: i18n.errors.required.body,
          },
          requiredMembers: missingReqElements,
        });
      }
      const attributes = [];
      const relationships = [];
      const imagesPromises = [];
      const imagesRealtionshipsBodies = [];
      const taxonomyPromises = [];
      const taxonomyKeys = [];
      const basicInput = {
        data: {
          type: `node--${bundle}`,
          attributes: {
            field_save_count: `${+fetchedNode.field_save_count + 1}`,
            moderation_state:
              btn === FormSubmitEnum.PUBLISH
                ? BundleJsonApiStates[bundle][BundleStates.REVIEW]
                : BundleJsonApiStates[bundle][BundleStates.DRAFT],
            default_langcode: locale === 'it',
            title: submittedData.title || '',
          },
          relationships: {},
        },
      };

      // Checking Link clean
      if (
        (!!submittedData.field_link_facebook &&
          !submittedData.field_link_facebook.includes('http')) ||
        (!!submittedData.field_link_instagram &&
          !submittedData.field_link_instagram.includes('http')) ||
        (!!submittedData.field_link_linkedin &&
          !submittedData.field_link_linkedin.includes('http')) ||
        (!!submittedData.field_link_pinterest &&
          !submittedData.field_link_pinterest.includes('http')) ||
        (!!submittedData.field_link_twitter &&
          !submittedData.field_link_twitter.includes('http'))
      ) {
        hideLoader();
        return setCustomErrors({
          i18n: {
            title: i18n.errors.linkError.title,
            body: i18n.errors.linkError.body,
          },
          requiredMembers: [],
        });
      }

      if (locale === 'en') {
        // Il prodotto inglese va filtrato da tutti i campi non traducibili
        allComponents?.map((c) => {
          const isTranslatable = c.isTranslatable;
          const isImg = c.field_type === FieldTypeEnum.IMAGE;
          if (isImg) {
            // è un immagine, va gestita la logica alt vs img translatables
            const isImgTranslatable = c.isImgTranslatable;
            const isAltTranslatable = c.isAltTranslatable;
            if (!isImgTranslatable) delete submittedData?.[c.name];
            if (!isAltTranslatable) delete submittedData?.[`alt_${c.name}_0`];
          } else {
            if (c.name !== 'title' && !isTranslatable)
              delete submittedData?.[c.name];
          }
        });
        additionalComponents.map((c) => {
          const isTranslatable = c.isTranslatable;

          const contentIdx = submittedData?.additionalContents?.findIndex(
            (ac) => ac?.[c.name]
          );

          if (contentIdx >= 0) {
            const isImg = c.field_type === FieldTypeEnum.IMAGE;
            if (isImg) {
              const isImgTranslatable = c.isImgTranslatable;
              const isAltTranslatable = c.isAltTranslatable;
              if (!isImgTranslatable)
                delete submittedData?.additionalContents[contentIdx]?.[c.name];
              if (!isAltTranslatable)
                delete submittedData?.additionalContents[contentIdx]?.[
                  `alt_${c.name}_0`
                ];
            } else {
              if (!isTranslatable)
                delete submittedData.additionalContents[contentIdx]?.[c.name];
            }
          }
        });
      }

      const dataSheets = submittedData?.additionalContents?.filter(
        (c) => c.name === ParagraphEnum.TECHNICAL_SHEET
      );

      if (dataSheets?.length) {
        const foundWithError = dataSheets.find(
          (ds) =>
            (!!ds.field_modello_3d && !ds.field_modello_3d.includes('http')) ||
            (!!ds.field_website && !ds.field_website.includes('http')) ||
            (!!ds.field_collezione && !ds.field_collezione.includes('http'))
        );
        if (foundWithError) {
          hideLoader();
          return setCustomErrors({
            i18n: {
              title: i18n.errors.urlFormat.title,
              body: i18n.errors.urlFormat.body,
            },
            requiredMembers: [],
          });
        }
      }

      allComponents.map((f) => {
        const type = f.field_type;
        // Se il tipo è diverso da uno di questi,possiamo aggiungere direttamente il valore agli attributes del node--product
        // altrimenti dobbiamo prima creare il paragrafo figlio e infine il prodotto padre
        type !== FieldTypeEnum.ENTITY_REFERENCE &&
        type !== FieldTypeEnum.ENTITY_REFERENCE_REVISIONS &&
        type !== FieldTypeEnum.FILE &&
        type !== FieldTypeEnum.IMAGE
          ? attributes.push(f)
          : relationships.push(f);
      });

      const editData = {};
      if (fetchedNode && fetchedNode.id) {
        Object.entries(submittedData).map(([k, v]) => {
          const vNotNullable = !v ? '' : v;
          const isRel = relationships.find((a) => a.name === k);
          const isImg =
            isRel?.field_type === FieldTypeEnum.FILE ||
            isRel?.field_type === FieldTypeEnum.IMAGE;

          const isArrayValue =
            Array.isArray(vNotNullable) && vNotNullable?.length;
          const isObjectValue =
            typeof vNotNullable === 'object' &&
            (!!vNotNullable?.value || !!vNotNullable.blocks?.length);
          const newField = !fetchedNode[k];
          const editField = fetchedNode[k] !== vNotNullable;
          const notImgRelationship = isRel && !isImg;

          if (
            (isArrayValue ||
              typeof vNotNullable === 'string' ||
              isObjectValue) &&
            (newField || editField || notImgRelationship)
          ) {
            // Non esiste proprio la chiave, è cambiato il valore, oppure è una relationships ==> va mandato in PATCH
            editData[k] = vNotNullable;
          }

          if (isImg) {
            // Se è un immagine e non è stata cambiata
            // deve comunque andare nel body, quindi forzo un cambiamento fake dell'alt
            const forcedAltKey = `alt_${k}_0`;
            const prevAltValue = submittedData[forcedAltKey] || '';
            editData[forcedAltKey] = prevAltValue;
          }
        });
        basicInput.data.id = fetchedNode.id;
      }

      // Gestiamo prima gli Additional Contents
      const alreadyFetchedComp = (savedData) =>
        fetchedNode.additionalContents?.find(
          (c) =>
            savedData.revisionId &&
            c.revisionId === savedData.revisionId &&
            c.name === savedData.name
        );

      const pushOldData = (name, id, revisionId, position) => {
        oldData.push({
          type: `paragraph--${name}`,
          id: id,
          meta: {
            target_revision_id: revisionId,
          },
        });
        positionedData.push({ id, position });
      };

      const getCompInfo = (savedData, key = []) => {
        const alreadyFetched = alreadyFetchedComp(savedData);
        const ac = alreadyFetched;
        const isEdit = key?.find(
          (k) =>
            savedData?.[k] !== ac?.[k] &&
            (Array.isArray(savedData[k])
              ? savedData[k].length > 0
              : !!savedData[k])
        );

        return { ac, isEdit };
      };

      const subEditReqBluePrint = [];
      const additionalContentsPromises = [];
      const oldData = [];
      const positionedData = [];

      // console.log(
      //   'LOG::   editData.additionalContents',
      //   editData.additionalContents
      // );
      editData.additionalContents?.map((c) => {
        if (c.name === ParagraphEnum.SD_BRAND_MULTITEXT) {
          const key = ['field_paragraph_simple_text'];
          const { ac, isEdit } = getCompInfo(c, key);
          if (!ac) {
            createSdBrandMultitext({
              submittedData: c,
              additionalComponents,
              bluePrintArray: subEditReqBluePrint,
              locale,
              position: c.position,
            });
          } else {
            if (!isEdit) {
              pushOldData(c.name, ac?.entityId, ac?.revisionId, c.position);
            } else {
              editSdBrandMultitext({
                submittedData: c,
                additionalComponents,
                entityId: ac?.entityId,
                locale,
                bluePrintArray: subEditReqBluePrint,
                position: c.position,
              });
            }
          }
        } else if (c.name === ParagraphEnum.TITLE_SECTION) {
          const key = ['field_section_title'];
          const { ac, isEdit } = getCompInfo(c, key);
          if (!ac) {
            createTitleSection({
              submittedData: c,
              additionalComponents,
              bluePrintArray: subEditReqBluePrint,
              locale,
              position: c.position,
            });
          } else {
            if (!isEdit) {
              pushOldData(c.name, ac?.entityId, ac?.revisionId, c.position);
            } else {
              editTitleSection({
                submittedData: c,
                additionalComponents,
                entityId: ac?.entityId,
                locale,
                bluePrintArray: subEditReqBluePrint,
                position: c.position,
              });
            }
          }
        } else if (c.name === ParagraphEnum.SDM_QUOTED_TEXT) {
          const key = ['field_testo_semplice'];
          const { ac, isEdit } = getCompInfo(c, key);
          if (!ac) {
            createQuotedText({
              submittedData: c,
              bluePrintArray: subEditReqBluePrint,
              locale,
              position: c.position,
            });
          } else {
            if (!isEdit) {
              pushOldData(c.name, ac?.entityId, ac?.revisionId, c.position);
            } else {
              editQuotedText({
                submittedData: c,
                entityId: ac?.entityId,
                locale,
                bluePrintArray: subEditReqBluePrint,
                position: c.position,
              });
            }
          }
        } else if (c.name === ParagraphEnum.YOUTUBE_VIDEO) {
          const key = ['field_url', 'field_didascalia'];
          const { ac, isEdit } = getCompInfo(c, key);
          if (!ac) {
            createYoutubeVideo({
              submittedData: c,
              additionalComponents,
              bluePrintArray: subEditReqBluePrint,
              locale,
              position: c.position,
            });
          } else {
            if (!isEdit) {
              pushOldData(c.name, ac?.entityId, ac?.revisionId, c.position);
            } else {
              editYoutubeVideo({
                submittedData: c,
                additionalComponents,
                entityId: ac?.entityId,
                locale,
                bluePrintArray: subEditReqBluePrint,
                position: c.position,
              });
            }
          }
        } else if (c.name === ParagraphEnum.VIMEO_VIDEO) {
          const key = ['field_vimeo_link'];
          const { ac, isEdit } = getCompInfo(c, key);
          if (!ac) {
            createVimeoVideo({
              submittedData: c,
              bluePrintArray: subEditReqBluePrint,
              locale,
              position: c.position,
            });
          } else {
            if (!isEdit) {
              pushOldData(c.name, ac?.entityId, ac?.revisionId, c.position);
            } else {
              editVimeoVideo({
                submittedData: c,
                entityId: ac?.entityId,
                locale,
                bluePrintArray: subEditReqBluePrint,
                position: c.position,
              });
            }
          }
        } else if (c.name === ParagraphEnum.VIRTUAL_SHOWROOM) {
          const key = [
            'field_link_vs',
            'field_titolo_vs',
            'field_immagine_vs',
            'alt_field_immagine_vs_0',
          ];
          const { ac, isEdit } = getCompInfo(c, key);

          const oldDataArray = fetchedNode.additionalContents;
          if (!ac) {
            additionalContentsPromises.push(
              limit(() =>
                createVirtualShowroom({
                  submittedData: c,
                  bluePrintArray: subEditReqBluePrint,
                  locale,
                  position: c.position,
                  createEntityFn: createEntity,
                })
              )
            );
          } else {
            if (!isEdit) {
              pushOldData(c.name, ac?.entityId, ac?.revisionId, c.position);
            } else {
              additionalContentsPromises.push(
                limit(() =>
                  editVirtualShowroom({
                    submittedData: c,
                    entityId: ac?.entityId,
                    locale,
                    bluePrintArray: subEditReqBluePrint,
                    position: c.position,
                    oldData: oldDataArray,
                    createEntityFn: createEntity,
                  })
                )
              );
            }
          }
        }
      });

      //  Gestione Contenuti con Immagine

      /**
       * Designer
       */

      const designerFields = editData.additionalContents?.filter(
        (c) => c.name === ParagraphEnum.DESIGNER
      );
      if (designerFields?.length) {
        designerFields.map((designerField) => {
          const key = [
            'alt_field_image_0',
            'field_desiger_name',
            'field_image',
          ];
          const { ac, isEdit } = getCompInfo(designerField, key);

          const oldDataArray = fetchedNode.additionalContents;
          if (!ac) {
            additionalContentsPromises.push(
              limit(() =>
                createDesigner({
                  submittedData: designerField,
                  createEntityFn: createEntity,
                  bluePrintArray: subEditReqBluePrint,
                  locale,
                  position: designerField.position,
                })
              )
            );
          } else {
            if (!isEdit) {
              pushOldData(
                designerField.name,
                ac?.entityId,
                ac?.revisionId,
                designerField.position
              );
            } else {
              additionalContentsPromises.push(
                limit(() =>
                  editDesigner({
                    submittedData: designerField,
                    oldData: oldDataArray,
                    createEntityFn: createEntity,
                    entityId: ac?.entityId,
                    bluePrintArray: subEditReqBluePrint,
                    locale,
                    position: designerField.position,
                  })
                )
              );
            }
          }
        });
      }

      /**
       * Free Image
       */
      const freeImgFields = editData.additionalContents?.filter(
        (c) => c.name === ParagraphEnum.FREE_IMAGE
      );
      if (freeImgFields?.length) {
        freeImgFields.map((freeImgField) => {
          const key = [
            'alt_field_immagine_0',
            'field_caption',
            'field_immagine',
          ];
          const { ac, isEdit } = getCompInfo(freeImgField, key);
          const oldDataArray = fetchedNode.additionalContents;
          if (!ac) {
            additionalContentsPromises.push(
              limit(() =>
                createFreeImage({
                  submittedData: freeImgField,
                  additionalComponents,
                  createEntityFn: createEntity,
                  bluePrintArray: subEditReqBluePrint,
                  locale,
                  position: freeImgField.position,
                })
              )
            );
          } else {
            if (!isEdit) {
              pushOldData(
                freeImgField.name,
                ac?.entityId,
                ac?.revisionId,
                freeImgField.position
              );
            } else {
              additionalContentsPromises.push(
                limit(() =>
                  editFreeImage({
                    submittedData: freeImgField,
                    oldData: oldDataArray,
                    additionalComponents,
                    createEntityFn: createEntity,
                    entityId: ac?.entityId,
                    bluePrintArray: subEditReqBluePrint,
                    locale,
                    position: freeImgField.position,
                  })
                )
              );
            }
          }
        });
      }

      /**
       * Free Image FullScreen
       */
      const freeImgFullScreenFields = editData.additionalContents?.filter(
        (c) => c.name === ParagraphEnum.FREE_IMAGE_FULL_SCREEN
      );
      if (freeImgFullScreenFields?.length) {
        freeImgFullScreenFields.map((freeImgFullScreenField) => {
          const key = [
            'alt_field_immagine_libera_full_0',
            'field_immagine_libera_full',
          ];
          const { ac, isEdit } = getCompInfo(freeImgFullScreenField, key);
          const oldDataArray = fetchedNode.additionalContents;
          if (!ac) {
            additionalContentsPromises.push(
              limit(() =>
                createFullScreenFreeImage({
                  submittedData: freeImgFullScreenField,
                  createEntityFn: createEntity,
                  bluePrintArray: subEditReqBluePrint,
                  locale,
                  position: freeImgFullScreenField.position,
                })
              )
            );
          } else {
            if (!isEdit) {
              pushOldData(
                freeImgFullScreenField.name,
                ac?.entityId,
                ac?.revisionId,
                freeImgFullScreenField.position
              );
            } else {
              additionalContentsPromises.push(
                limit(() =>
                  editFullScreenFreeImage({
                    submittedData: freeImgFullScreenField,
                    oldData: oldDataArray,
                    createEntityFn: createEntity,
                    entityId: ac?.entityId,
                    bluePrintArray: subEditReqBluePrint,
                    locale,
                    position: freeImgFullScreenField.position,
                  })
                )
              );
            }
          }
        });
      }

      /**
       * Technical Sheet
       */
      const technicalSheetFields = editData.additionalContents?.filter(
        (c) => c.name === ParagraphEnum.TECHNICAL_SHEET
      );
      if (technicalSheetFields?.length) {
        technicalSheetFields.map((technicalSheetField) => {
          const key = [
            'field_anno',
            'field_caratteristiche',
            'field_collezione',
            'field_descrizione',
            'field_designer',
            'field_dimensioni',
            'field_materiali',
            'field_modello_3d',
            'field_processo_produttivo',
            'field_sheet_image',
            'field_website',
            'alt_field_sheet_image_0',
            'alt_field_modello_3d_0',
            'alt_field_website_0',
            'alt_field_collezione_0',
          ];
          const { ac, isEdit } = getCompInfo(technicalSheetField, key);
          const oldDataArray = fetchedNode.additionalContents;
          if (!ac) {
            additionalContentsPromises.push(
              limit(() =>
                createTechnicalSheet({
                  submittedData: technicalSheetField,
                  createEntityFn: createEntity,
                  bluePrintArray: subEditReqBluePrint,
                  locale,
                  position: technicalSheetField.position,
                })
              )
            );
          } else {
            if (!isEdit) {
              pushOldData(
                technicalSheetField.name,
                ac?.entityId,
                ac?.revisionId,
                technicalSheetField.position
              );
            } else {
              additionalContentsPromises.push(
                limit(() =>
                  editTechnicalSheet({
                    submittedData: technicalSheetField,
                    oldData: oldDataArray,
                    createEntityFn: createEntity,
                    entityId: ac?.entityId,
                    bluePrintArray: subEditReqBluePrint,
                    locale,
                    position: technicalSheetField.position,
                  })
                )
              );
            }
          }
        });
      }

      /**
       * Slider
       */
      const sliderFields = editData.additionalContents?.filter(
        (c) => c.name === ParagraphEnum.SLIDER_DETAIL
      );
      if (sliderFields?.length) {
        sliderFields.map((sliderField) => {
          const adjustedOldRevision = sliderField.oldRevisionId
            ?.map((oldId, idx) =>
              sliderField.filePositions.find((position) => position === idx) >=
              0
                ? oldId
                : ''
            )
            .filter((id) => !!id);
          sliderField.oldRevisionId = adjustedOldRevision;

          const key = [];
          const { ac } = getCompInfo(sliderField, key);

          const oldDataArray = fetchedNode.additionalContents;

          const oldComponent = oldDataArray?.find(
            (c) =>
              c.name === ParagraphEnum.SLIDER_DETAIL &&
              c.revisionId === sliderField.revisionId
          );

          // const otherLangComponent = otherLangAdditionalEntity.additionalContents?.find(
          //   (c) =>
          //     c.name === ParagraphEnum.SLIDER_DETAIL &&
          //     c.entityId === sliderField.id
          // );

          const isNotEditImgs =
            sliderField?.bp_image_field?.length ===
            oldComponent?.bp_image_field?.length
              ? sliderField.bp_image_field?.every((img) =>
                  oldComponent?.bp_image_field?.find((i) => i === img)
                )
              : false;

          const isNotEditAlt =
            sliderField?.fileAlts?.length === oldComponent?.fileAlts?.length
              ? sliderField.fileAlts?.every((alt, idx) => {
                  const oldAlt = oldComponent?.fileAlts?.[idx];
                  return oldAlt === alt;
                })
              : null;

          const isNotEditCaption =
            sliderField?.fileCaptions?.length ===
            oldComponent?.fileCaptions?.length
              ? sliderField.fileCaptions?.every((caption, idx) => {
                  const parsedNewCaption = caption
                    ? typeof caption !== 'string'
                      ? JSON.stringify(caption)
                      : caption
                    : '';
                  const oldCaption = oldComponent?.fileCaptions?.[idx];
                  const parsedOldCaption = oldCaption
                    ? typeof oldCaption !== 'string'
                      ? JSON.stringify(oldCaption)
                      : oldCaption
                    : '';
                  return parsedOldCaption === parsedNewCaption;
                })
              : null;

          const isNotEditPositions =
            sliderField?.filePositions?.length ===
            oldComponent?.filePositions?.length
              ? sliderField.filePositions?.every((position, idx) => {
                  const oldPosition = oldComponent?.filePositions?.[idx];
                  return oldPosition === position;
                })
              : null;
          // const areNotRevisionIdsUpdate =
          //   sliderField?.oldRevisionId?.length ===
          //   otherLangComponent?.oldRevisionId?.length
          //     ? sliderField.oldRevisionId?.every((revId, idx) => {
          //         const otherRevisionIdString =
          //           otherLangComponent?.oldRevisionId?.[idx];
          //         return +otherRevisionIdString <= +revId;
          //       })
          //     : null;

          if (!ac) {
            additionalContentsPromises.push(
              limit(() =>
                createSlider({
                  submittedData: sliderField,
                  additionalComponents,
                  createEntityFn: createEntity,
                  bluePrintArray: subEditReqBluePrint,
                  locale,
                  position: sliderField.position,
                })
              )
            );
          } else {
            if (
              isNotEditImgs &&
              isNotEditAlt &&
              isNotEditCaption &&
              isNotEditPositions
              // areNotRevisionIdsUpdate
            ) {
              pushOldData(
                sliderField.name,
                ac?.entityId,
                ac?.revisionId,
                sliderField.position
              );
            } else {
              additionalContentsPromises.push(
                limit(() =>
                  editSlider({
                    submittedData: sliderField,
                    oldData: oldDataArray,
                    additionalComponents,
                    createEntityFn: createEntity,
                    editEntityFn: editEntity,
                    entityId: ac?.entityId,
                    bluePrintArray: subEditReqBluePrint,
                    locale,
                    position: sliderField.position,
                    // otherRevisionsId: otherLangComponent?.oldRevisionId,
                  })
                )
              );
            }
          }
        });
      }

      const updatedData = positionedData
        .sort((a, b) => a.position - b.position)
        .map((d) => oldData?.find((da) => da.id === d.id));

      await Promise.all(additionalContentsPromises);
      if (subEditReqBluePrint.length) {
        // console.log(
        //   'LOG::   subEditReqBluePrint',
        //   subEditReqBluePrint.map((s) => JSON.parse(s.body))
        // );
        const res = await createSubReq({
          bluePrints: subEditReqBluePrint,
          langCode: locale,
        });
        if (res && res?.responses?.length) {
          // console.log('LOG::   res.responses', res.responses);
          const data = res.responses
            .map(
              (r) =>
                r.data?.id && {
                  type: r.data.type,
                  id: r.data.id,
                  meta: {
                    target_revision_id:
                      r.data.attributes.drupal_internal__revision_id,
                  },
                }
            )
            .filter((r) => !!r);

          data.map((d) => {
            const foundPosition = res?.positions?.find(
              (p) => p.id === d.id
            )?.position;
            updatedData.splice(foundPosition, 0, d);
          });
        }
      }
      const updatedRevisions = otherLangAdditionalEntity?.additionalContents
        ?.length
        ? updatedData.map((d) => {
            const alreadyOtherLang =
              otherLangAdditionalEntity?.additionalContents?.find(
                (c) => c.entityId === d.id
              );
            const currentRevisionId = d.meta.target_revision_id;
            const targetRevisionId =
              alreadyOtherLang &&
              +alreadyOtherLang?.revisionId > currentRevisionId
                ? +alreadyOtherLang?.revisionId
                : currentRevisionId;
            return {
              ...d,
              meta: {
                target_revision_id: targetRevisionId,
              },
            };
          })
        : updatedData;

      basicInput.data.relationships[
        isProduct ? 'field_content' : 'field_contents'
      ] = { data: updatedRevisions };

      Object.entries(editData).map(([k, v]) => {
        const foundInAttributes = attributes.find((a) => a.name === k);
        const foundInRelationships = relationships.find((a) => a.name === k);
        const value = v === '0' ? false : v === '1' ? true : v;
        // Iniziamo gestendo le immagini e relativi alt (molto rognoso)
        if (k.includes('alt_')) {
          const imgKey = k.split('_').slice(1, -1).join('_');
          if (!editData?.[imgKey]) {
            // è cambiato l'alternative text di un immagine ma non l'ímmagine stessa
            const imgId = fetchedNode[`${imgKey}_id`];
            basicInput.data.relationships[imgKey] = {
              data: {
                type: 'file--file',
                id: imgId,
                meta: {
                  alt: value || imgKey,
                },
              },
            };
          }
        }
        if (foundInAttributes) {
          // Il valore è un attributes, quindi dobbiamo solo controllare se ha un format da dichiarare
          basicInput.data.attributes[k] = foundInAttributes.format
            ? {
                value:
                  typeof v === 'string' && !!v
                    ? draftToHtml(JSON.parse(v))
                    : draftToHtml(v),
                format: foundInAttributes.format,
              }
            : value;
        } else if (foundInRelationships?.field_type === FieldTypeEnum.IMAGE) {
          // Se è un'immagine, va prima caricata su Drupal
          if (value.length) {
            value.map((v, idx) => {
              const _copy = new File([v], v.name);
              if (_copy.preview) delete _copy.preview;
              imagesPromises.push(
                limit(() =>
                  createEntity({
                    langCode: locale,
                    customHeaders: {
                      'Content-Type': 'application/octet-stream',
                      Accept: 'application/vnd.api+json',
                      'Content-Disposition': `file; filename="${_copy.name}"`,
                    },
                    body: _copy,
                    fieldName: k,
                    useAxios: false,
                    bundle: bundle,
                    ...(!isProduct && {
                      uuid: fetchedNode?.id,
                    }),
                  })
                )
              );
              // Poi va aggiunta alle relationships
              imagesRealtionshipsBodies.push({
                key: k,
                alt:
                  editData?.[`alt_${k}_${idx}`] ||
                  fetchedNode?.[`alt_${k}_${idx}`] ||
                  _copy.name,
              });
            });
          }
        } else if (
          foundInRelationships?.field_type === FieldTypeEnum.ENTITY_REFERENCE
        ) {
          const getObject = (val, ent) => ({
            type: `${ent.formEntityType}--${ent.formEntityBundle}`,
            id: val.entityId,
          });

          if (k === 'field_private_collezione') {
            // È una colezione (taxonomy_term),
            // Se essite va su
            if (value.value) {
              const parsedValue = {
                ...value,
                entityId: value.entityId || value.value,
              };
              basicInput.data.relationships[k] = {
                data: getObject(parsedValue, foundInRelationships),
              };
            } else {
              // Altrimenti prima la creo
              if (value) {
                const input = {
                  data: {
                    type: `${foundInRelationships.formEntityType}--${foundInRelationships.formEntityBundle}`,
                    attributes: {
                      name: value,
                    },
                  },
                };

                taxonomyPromises.push(
                  limit(() =>
                    createEntity({
                      useLoader: false,
                      langCode: locale,
                      body: input,
                      entity: foundInRelationships.formEntityType,
                      bundle: foundInRelationships.formEntityBundle,
                    })
                  )
                );
                taxonomyKeys.push(k);
              } else {
                basicInput.data.relationships[k] = {};
              }
            }
          } else {
            basicInput.data.relationships[k] = {
              data:
                // Il Brand è un caso troppo particolare, va gestito separatamente
                typeof value === 'string' && k === 'field_brand'
                  ? {
                      type: 'node--scheda_espositore',
                      id: value,
                    }
                  : value && Array.isArray(value) && value.length
                  ? value.map((v) => getObject(v, foundInRelationships))
                  : getObject(value, foundInRelationships),
            };
          }
        }
      });

      if (taxonomyPromises.length) {
        const taxonomyRes = await Promise.all(taxonomyPromises);
        taxonomyRes.map((r, idx) => {
          const taxId = r.data.id;
          const taxType = r.data.type;
          basicInput.data.relationships[taxonomyKeys[idx]] = {
            data: {
              type: taxType,
              id: taxId,
            },
          };
        });
      }

      if (imagesPromises.length) {
        // 1 Crea tutte le immagini
        const res = await Promise.all(imagesPromises);
        // 2 Recupera tutti gli id dalle immagini per crearne la corrispondente relationships
        res.map((r, idx) => {
          const imageId = r.id;
          const field_key = imagesRealtionshipsBodies[idx].key;
          const field_alt = imagesRealtionshipsBodies[idx].alt;
          basicInput.data.relationships[field_key] = {
            data: {
              type: 'file--file',
              id: imageId,
              meta: {
                alt: field_alt || field_key,
              },
            },
          };
        });
      }
      // Manual handling attributes to fix a Drupal bug ==> horrible, but we have to do this
      // Here we have to force the attributes inside the payload to solve a Drupal issue
      Object.entries(submittedData).map(([k, v]) => {
        const foundInAttributes = attributes.find((a) => a.name === k);
        const value = v === '0' ? false : v === '1' ? true : v;
        if (foundInAttributes) {
          basicInput.data.attributes[k] = foundInAttributes.format
            ? {
                value:
                  typeof v === 'string' && !!v
                    ? draftToHtml(JSON.parse(v))
                    : draftToHtml(v),
                format: foundInAttributes.format,
              }
            : value;
        }
      });
      // console.log('LOG::   basicInput', basicInput);
      const hasRelationshipsInput = !!Object.keys(basicInput.data.relationships)
        .length;
      if (!hasRelationshipsInput) delete basicInput.data.relationships;
      if (
        hasRelationshipsInput ||
        Object.keys(basicInput.data.attributes).length > 1
      ) {
        const res = await editEntity({
          useLoader: false,
          langCode: locale,
          body: basicInput,
          entity: 'node',
          bundle: bundle,
          entityId: fetchedNode.id,
        });
        if (!res.data) {
          hideLoader();
          return setCustomErrors({
            i18n: {
              title: i18n.errors.generalError.title,
              body: i18n.errors.generalError.body,
            },
            requiredMembers: [],
          });
        }
        // return hideLoader();
        if (redirectLink) {
          window.location.href = redirectLink;
        } else {
          location.reload();
        }
      }
    } catch (error) {
      setCustomErrors({
        i18n: {
          title: i18n.errors.generalError.title,
          body: i18n.errors.generalError.body,
        },
        requiredMembers: [],
      });
      logApiError(error, 'edit-data');
      hideLoader();
    } finally {
      // hideLoader();
      setBtnClicked('');
    }
  };

  const handleHeaderBtnClick = (btn, values) => {
    setBtnClicked(btn);
    setSubmittedData(values);
    if (btn === FormSubmitEnum.PREVIEW) setOpen(false);
  };

  const parseDraftAfterPreview = (value) => {
    if (value && typeof value !== 'string') {
      const _body = draftToHtml(value);
      return JSON.stringify(parseHtmlToDraw(_body));
    }
    return value;
  };

  const handleOpenDrawerClick = (resetFn) => {
    if (submittedData?.body) {
      submittedData.body = parseDraftAfterPreview(submittedData?.body);
    }
    if (submittedData?.field_didascalia) {
      submittedData.field_didascalia = parseDraftAfterPreview(
        submittedData?.field_didascalia
      );
    }
    if (submittedData?.additionalContents?.length) {
      submittedData?.additionalContents.map((ac, idx) => {
        // Bisogna per forza distinguere ogni RichText caso per caso, perchè le chiavi sono univoche
        const isBrandMultitext = ac?.name === ParagraphEnum.SD_BRAND_MULTITEXT;
        if (isBrandMultitext) {
          submittedData.additionalContents[idx].field_paragraph_simple_text =
            parseDraftAfterPreview(ac?.field_paragraph_simple_text);
        }
        const isYoutubeRichText = ac?.name === ParagraphEnum.YOUTUBE_VIDEO;
        if (isYoutubeRichText) {
          submittedData.additionalContents[idx].field_didascalia =
            parseDraftAfterPreview(ac?.field_didascalia);
        }
        const isFreeImageText = ac?.name === ParagraphEnum.FREE_IMAGE;
        if (isFreeImageText) {
          submittedData.additionalContents[idx].field_caption =
            parseDraftAfterPreview(ac?.field_caption);
        }
        const isSlider = ac?.name === ParagraphEnum.SLIDER_DETAIL;
        if (isSlider) {
          submittedData.additionalContents[idx].fileCaptions =
            ac?.fileCaptions?.map((fb) => parseDraftAfterPreview(fb));
        }
      });
    }
    submittedData && resetFn(submittedData);
    setOpen(true);
  };

  return {
    getCsrfToken,
    createEntity,
    editEntity,
    getEntity,
    getViews,
    listEntityFieldsForm,
    listEntityReferenceRevisionsFieldsForm,
    onSubmitData,
    onEditData,
    handleHeaderBtnClick,
    handleOpenDrawerClick,
    setCustomErrors,
    setPreviewData,
    parseDraftAfterPreview,
    setOpen,
    brandName,
    generalAccordionComponents,
    basicAccordionComponents,
    additionalComponents,
    requiredFields,
    previewData,
    customErrors,
    open,
    btnCliked,
    submittedData,
    additionalContents,
  };
};

export { useEntities };
